// src/pages/MaterialsPage.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useData } from "../DataContext";
import { apiRequest } from "../api/http";
import { Card } from "../components/ui/Card";
import { TableShell, CellClamp } from "../components/ui/TableShell";
import { projectDisplayLabelPretty } from "../utils/format";

type ProcurementOwner = "contractor" | "client";

// ✅ Match DB strings (025_create_project_materials.sql)
type MaterialStatus =
  | "Not Ordered"
  | "Ordered"
  | "In Transit"
  | "Delivered"
  | "On Site"
  | "Installed";

type MaterialLibraryItem = {
  id: string;
  tenant_id: string; // 'global' or tenant
  template_code: string;
  template_name: string;

  material_name: string;
  material_name_lc: string;

  category: string; // Bathroom, Kitchen, Flooring etc (your clarified meaning)
  procurement_owner: ProcurementOwner;

  lead_time_days?: number | null;
  link_url?: string | null;
  default_sort_order?: number | null;

  status: "active" | "inactive" | string;
  created_at?: string;
  updated_at?: string;
};

type ProjectMaterial = {
  id: string;
  tenant_id: string;
  project_id: string;

  material_library_id?: string | null;
  template_code?: string | null;

  category?: string | null;
  material_name: string;

  procurement_owner: ProcurementOwner;
  status: MaterialStatus;

  lead_time_days?: number | null;

  needed_by_date?: string | null; // YYYY-MM-DD
  actual_delivery_date?: string | null; // YYYY-MM-DD

  vendor?: string | null;
  order_ref?: string | null;
  tracking_ref?: string | null;
  delivery_location?: string | null;

  notes?: string | null;
  is_internal?: number | null;

  // 032 migration
  quantity?: number | null;
  link_url?: string | null;

  created_at?: string;
  updated_at?: string;
};

type MaterialTemplate = {
  id: string;
  tenant_id: string; // global or tenant
  template_code: string;
  template_name: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
};

const STATUS_OPTIONS: MaterialStatus[] = [
  "Not Ordered",
  "Ordered",
  "In Transit",
  "Delivered",
  "On Site",
  "Installed",
];

const moneylessTrim = (s: any) => String(s ?? "").trim();
const lc = (s: any) => String(s ?? "").trim().toLowerCase();
const makeTemplateCode = (name: string) => {
  const base = lc(name)
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);

  const suffix = String(Date.now()).slice(-4); // avoids collisions without user thinking about it
  return base ? `${base}_${suffix}` : `template_${suffix}`;
};

const isYmd = (s?: string | null) => {
  if (!s) return true;
  return /^\d{4}-\d{2}-\d{2}$/.test(String(s));
};

type EditDraft = {
  category: string;
  material_name: string;
  procurement_owner: ProcurementOwner;
  status: MaterialStatus;
  needed_by_date: string;
  actual_delivery_date: string;
  notes: string;
  quantity: string;
  link_url: string;
};

const BLANK_DRAFT: EditDraft = {
  category: "",
  material_name: "",
  procurement_owner: "contractor",
  status: "Not Ordered",
  needed_by_date: "",
  actual_delivery_date: "",
  notes: "",
  quantity: "",
  link_url: "",
};

// a small helper to show friendlier constraint-ish errors without changing backend
const friendlyDeleteMessage = (raw: any, fallback: string) => {
  const msg = String(raw?.message || raw?.error || raw || fallback || "").trim();
  const low = msg.toLowerCase();

  // common patterns for FK/constraint/reference failures across sqlite/d1
  if (
    low.includes("foreign key") ||
    low.includes("constraint") ||
    low.includes("referenc") ||
    low.includes("in use") ||
    low.includes("cannot delete") ||
    low.includes("failed to delete") ||
    low.includes("sql")
  ) {
    return (
      "Can’t delete this material because it’s currently referenced by a template and/or a project.\n\n" +
      "Fix:\n" +
      "• Remove it from any templates first (Template contents → Remove)\n" +
      "• Then try deleting again from the Library list."
    );
  }

  return msg || fallback;
};

export default function MaterialsPage() {
  const { t } = useTranslation();
  const { tenantId, projects, clients } = useData();

  // Always send tenant header for Materials APIs (fixes templates not loading)
  const api = useCallback(
    async <T,>(method: string, url: string, opts?: any) => {
      const next = { ...(opts || {}) };
      next.headers = { ...(next.headers || {}), "x-tenant-id": tenantId };
      return apiRequest<T>(method as any, url, next);
    },
    [tenantId]
  );

  /* ======================
     Page context
  ====================== */
  const [projectId, setProjectId] = useState<string>("");
  const prettyProject = useMemo(() => {
    const p = (projects || []).find((x: any) => x.id === projectId);
    if (!p) return "";
    return projectDisplayLabelPretty(p as any, clients as any);
  }, [projects, clients, projectId]);

  /* ======================
     Library data
  ====================== */
  const [libBusy, setLibBusy] = useState(false);
  const [libError, setLibError] = useState<string | null>(null);
  const [library, setLibrary] = useState<MaterialLibraryItem[]>([]);

  const loadLibrary = useCallback(async () => {
    try {
      setLibBusy(true);
      setLibError(null);

      const res = await api<{ ok: true; data: MaterialLibraryItem[] }>(
        "GET",
        `/api/material_library?status=active`
      );

      if (!(res as any)?.ok)
        throw new Error((res as any)?.error || "Failed to load material library");

      setLibrary(((res as any)?.data || []) as any);
    } catch (e: any) {
      setLibError(e?.message || "Failed to load material library");
      setLibrary([]);
    } finally {
      setLibBusy(false);
    }
  }, [api]);

  useEffect(() => {
    loadLibrary();
  }, [loadLibrary]);

  /* ======================
     Templates (new)
  ====================== */
  const [tmplBusy, setTmplBusy] = useState(false);
  const [tmplError, setTmplError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<MaterialTemplate[]>([]);
  const [activeTemplateId, setActiveTemplateId] = useState<string>("");

  const tenantLc = String(tenantId || "").trim().toLowerCase();
  const isSystemTenant = tenantLc === "system";
  const isNJDCTenant = tenantLc === "njdc";

    // SYSTEM + NJDC can manage global library rows
  const canManageGlobalLibrary = isSystemTenant || isNJDCTenant;


  const canEditTemplate = useCallback(
    (t: MaterialTemplate) => {
      if (t.tenant_id === tenantId) return true;
      if ((isSystemTenant || isNJDCTenant) && t.tenant_id === "global") return true;
      return false;
    },
    [tenantId, isSystemTenant, isNJDCTenant]
  );

  const loadTemplates = useCallback(async () => {
    try {
      setTmplBusy(true);
      setTmplError(null);

      const res = await api<any>("GET", `/api/material_templates`);
      if (!res?.ok) throw new Error(res?.error || "Failed to load templates");

      // tolerate different payload shapes
      const rows =
        (res?.data as any[]) ||
        (res?.templates as any[]) ||
        (res?.items as any[]) ||
        [];

      setTemplates(Array.isArray(rows) ? (rows as any) : []);
    } catch (e: any) {
      setTmplError(e?.message || "Failed to load templates");
      setTemplates([]);
    } finally {
      setTmplBusy(false);
    }
  }, [api]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const activeTemplate = useMemo(() => {
    return (templates || []).find((tmpl) => tmpl.id === activeTemplateId) || null;
  }, [templates, activeTemplateId]);

  /* ======================
     Template items (membership)
  ====================== */
  const [tmplItemsBusy, setTmplItemsBusy] = useState(false);
  const [tmplItemsError, setTmplItemsError] = useState<string | null>(null);
  const [templateItems, setTemplateItems] = useState<MaterialLibraryItem[]>([]);

  const loadTemplateItems = useCallback(
    async (templateId: string) => {
      const tid = (templateId || "").trim();
      if (!tid) {
        setTemplateItems([]);
        return;
      }
      try {
        setTmplItemsBusy(true);
        setTmplItemsError(null);

        const res = await api<{ ok: true; data: MaterialLibraryItem[] }>(
          "GET",
          `/api/material_templates/${encodeURIComponent(tid)}/items`
        );

        setTemplateItems((res.data || []) as any);
      } catch (e: any) {
        setTmplItemsError(e?.message || "Failed to load template items");
        setTemplateItems([]);
      } finally {
        setTmplItemsBusy(false);
      }
    },
    [api]
  );

  useEffect(() => {
    loadTemplateItems(activeTemplateId);
  }, [activeTemplateId, loadTemplateItems]);

    // Quick lookup: library row by id (used to label project materials as GLOBAL)
  const libraryById = useMemo(() => {
    const map = new Map<string, MaterialLibraryItem>();
    (library || []).forEach((m) => {
      if (m?.id) map.set(String(m.id), m);
    });
    return map;
  }, [library]);

  const isGlobalLibraryMaterial = useCallback(
    (pm: ProjectMaterial) => {
      const id = String(pm.material_library_id || "").trim();
      if (!id) return false;
      const lib = libraryById.get(id);
      return String(lib?.tenant_id || "") === "global";
    },
    [libraryById]
  );

  const normalizeUrl = (raw?: string | null) => {
    const u = String(raw || "").trim();
    if (!u) return "";
    // If user pasted without scheme, make it clickable
    if (/^https?:\/\//i.test(u)) return u;
    return `https://${u}`;
  };

  const templateItemIdSet = useMemo(() => {
    const s = new Set<string>();
    (templateItems || []).forEach((m) => m?.id && s.add(m.id));
    return s;
  }, [templateItems]);

  /* ======================
     Project materials data
  ====================== */
  const [projBusy, setProjBusy] = useState(false);
  const [projError, setProjError] = useState<string | null>(null);
  const [projectMaterials, setProjectMaterials] = useState<ProjectMaterial[]>([]);

  const loadProjectMaterials = useCallback(
    async (pid: string) => {
      const p = (pid || "").trim();
      if (!p) {
        setProjectMaterials([]);
        return;
      }
      try {
        setProjBusy(true);
        setProjError(null);

        const res = await apiRequest<{ ok: true; data: ProjectMaterial[] }>(
          "GET",
          `/api/project_materials?projectId=${encodeURIComponent(p)}`,
          { tenantId }
        );

        setProjectMaterials((res.data || []) as any);
      } catch (e: any) {
        setProjError(e?.message || "Failed to load project materials");
        setProjectMaterials([]);
      } finally {
        setProjBusy(false);
      }
    },
    [tenantId]
  );

  useEffect(() => {
    loadProjectMaterials(projectId);
  }, [projectId, loadProjectMaterials]);

  /* ======================
     Apply template to project (v2)
  ====================== */
  const [applyBusy, setApplyBusy] = useState(false);

  const applyTemplateToProjectV2 = useCallback(async () => {
    if (!projectId) return window.alert(t("materials.selectProjectFirst"));
    if (!activeTemplateId) return window.alert(t("materials.selectTemplateBulk"));

    try {
      setApplyBusy(true);
      setProjError(null);

      const res = await apiRequest<any>("POST", `/api/materials/apply-template`, {
        tenantId,
        body: { project_id: projectId, template_id: activeTemplateId },
      });

      if (!(res as any)?.ok)
        throw new Error((res as any)?.error || "Failed to apply template");

      await loadProjectMaterials(projectId);
    } catch (e: any) {
      setProjError(e?.message || "Failed to apply template");
    } finally {
      setApplyBusy(false);
    }
  }, [tenantId, projectId, activeTemplateId, loadProjectMaterials, t]);

  /* ======================
     Template Builder Modal (single modal, no extra windows)
  ====================== */
  const [showTemplateBuilder, setShowTemplateBuilder] = useState(false);
    // Modal UX: collapse least-used cards by default
  const [newTemplateOpen, setNewTemplateOpen] = useState(false);
  const [addCustomOpen, setAddCustomOpen] = useState(false);


  // template list search + create
  const [tmplSearch, setTmplSearch] = useState("");
  const templatesFiltered = useMemo(() => {
    const q = lc(tmplSearch);
    if (!q) return templates || [];
    return (templates || []).filter((t) => {
      const text = `${t.template_name} ${t.template_code} ${t.tenant_id}`.toLowerCase();
      return text.includes(q);
    });
  }, [templates, tmplSearch]);

  const [newTmplName, setNewTmplName] = useState("");
  const [tmplCreateBusy, setTmplCreateBusy] = useState(false);

  const createTemplate = useCallback(async () => {
    const name = moneylessTrim(newTmplName);
    if (!name) return window.alert(t("materials.templateNameRequired"));
    const code = makeTemplateCode(name);

    try {
      setTmplCreateBusy(true);
      setTmplError(null);

      const res = await api<any>("POST", `/api/material_templates`, {
        body: { template_name: name, template_code: code },
      });

      if (!(res as any)?.ok)
        throw new Error((res as any)?.error || "Failed to create template");

      await loadTemplates();

      // select it
      const id = (res as any)?.data?.id;
      if (id) setActiveTemplateId(String(id));

      setNewTmplName("");
    } catch (e: any) {
      setTmplError(e?.message || "Failed to create template");
    } finally {
      setTmplCreateBusy(false);
    }
  }, [newTmplName, loadTemplates, api, t]);

  // rename/delete template
  const [editTmplId, setEditTmplId] = useState<string | null>(null);
  const [editTmplName, setEditTmplName] = useState("");
  const [editTmplCode, setEditTmplCode] = useState("");
  const [tmplSaveBusy, setTmplSaveBusy] = useState(false);

  const beginEditTemplate = useCallback((tmpl: MaterialTemplate) => {
    setTmplError(null);
    setEditTmplId(tmpl.id);
    setEditTmplName(tmpl.template_name || "");
    setEditTmplCode(tmpl.template_code || "");
  }, []);

  const cancelEditTemplate = useCallback(() => {
    setEditTmplId(null);
    setEditTmplName("");
    setEditTmplCode("");
  }, []);

  const saveTemplateEdit = useCallback(async () => {
    if (!editTmplId) return;
    const name = moneylessTrim(editTmplName);
    const code = moneylessTrim(editTmplCode);
    if (!name) return window.alert(t("materials.templateNameRequired"));
    if (!code) return window.alert(t("materials.templateCodeRequired"));

    try {
      setTmplSaveBusy(true);
      setTmplError(null);

      const res = await api<any>(
        "PATCH",
        `/api/material_templates/${encodeURIComponent(editTmplId)}`,
        { body: { template_name: name, template_code: code } }
      );

      if (!(res as any)?.ok)
        throw new Error((res as any)?.error || "Failed to update template");

      await loadTemplates();

      // keep selection stable
      setActiveTemplateId((prev) => prev || editTmplId);

      cancelEditTemplate();
    } catch (e: any) {
      setTmplError(e?.message || "Failed to update template");
    } finally {
      setTmplSaveBusy(false);
    }
  }, [
    editTmplId,
    editTmplName,
    editTmplCode,
    loadTemplates,
    cancelEditTemplate,
    api,
    t,
  ]);

  const deleteTemplate = useCallback(
    async (tmpl: MaterialTemplate) => {
      const editable = canEditTemplate(tmpl);
      if (!editable) return;

      const ok = window.confirm(t("materials.confirmDeleteTemplate", { name: tmpl.template_name, code: tmpl.template_code }));
      if (!ok) return;

      try {
        setTmplSaveBusy(true);
        setTmplError(null);

        const res = await api<any>(
          "DELETE",
          `/api/material_templates/${encodeURIComponent(tmpl.id)}`
        );

        if (!(res as any)?.ok)
          throw new Error((res as any)?.error || "Failed to delete template");

        // clear selection if it was active
        setActiveTemplateId((prev) => (prev === tmpl.id ? "" : prev));

        await loadTemplates();
        setTemplateItems([]);
      } catch (e: any) {
        setTmplError(e?.message || "Failed to delete template");
      } finally {
        setTmplSaveBusy(false);
      }
    },
    [canEditTemplate, loadTemplates, api, t]
  );

  // Template builder: browse library + add to template
  const [builderCategory, setBuilderCategory] = useState<string>("");
  const [builderSearch, setBuilderSearch] = useState<string>("");
  const [builderSelectedLibIds, setBuilderSelectedLibIds] = useState<string[]>([]);
  const [builderAddBusy, setBuilderAddBusy] = useState(false);

  const categories = useMemo(() => {
    const set = new Set<string>();
    (library || []).forEach((m) => {
      const c = moneylessTrim(m.category);
      if (c) set.add(c);
    });
    return Array.from(set.values()).sort((a, b) => a.localeCompare(b));
  }, [library]);

  const libraryFiltered = useMemo(() => {
    const q = lc(builderSearch);
    const cat = moneylessTrim(builderCategory);

    let base = (library || []).filter((m) => String(m.status || "") === "active");
    if (cat) base = base.filter((m) => moneylessTrim(m.category) === cat);

    if (q) {
      base = base.filter((m) => {
        const text = `${m.material_name} ${m.category} ${m.template_name} ${m.template_code} ${m.procurement_owner} ${m.tenant_id}`.toLowerCase();
        return text.includes(q);
      });
    }

    // sort: category, then name
    base.sort((a, b) => {
      const ac = lc(a.category);
      const bc = lc(b.category);
      if (ac !== bc) return ac.localeCompare(bc);
      return lc(a.material_name).localeCompare(lc(b.material_name));
    });

    // DEDUPE: show one row per (category + owner + material_name)
    // prefer rows that match the active template_code, then prefer tenant over global
    const preferredTemplateCode = activeTemplate?.template_code || "";
    const pickScore = (m: any) => {
      let s = 0;
      if (preferredTemplateCode && m.template_code === preferredTemplateCode) s += 100;
      if (m.tenant_id === tenantId) s += 10;
      return s;
    };

    const map = new Map<string, any>();
    for (const m of base) {
      const key = `${lc(m.category)}|${m.procurement_owner}|${lc(m.material_name)}`;
      const prev = map.get(key);
      if (!prev || pickScore(m) > pickScore(prev)) map.set(key, m);
    }

    return Array.from(map.values());
  }, [library, builderCategory, builderSearch, activeTemplate, tenantId]);

  const toggleBuilderPick = useCallback((id: string) => {
    setBuilderSelectedLibIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const selectAllBuilderFiltered = useCallback(() => {
    const ids = (libraryFiltered || []).map((m) => m.id).filter(Boolean);
    setBuilderSelectedLibIds((prev) => {
      const set = new Set(prev);
      ids.forEach((id) => set.add(id));
      return Array.from(set.values());
    });
  }, [libraryFiltered]);

  const clearBuilderSelected = useCallback(() => setBuilderSelectedLibIds([]), []);

  const addSelectedToTemplate = useCallback(async () => {
    if (!activeTemplateId) return window.alert(t("materials.selectTemplateBulk"));
    if (!builderSelectedLibIds.length) return window.alert(t("materials.selectAtLeastOneLib"));

    try {
      setBuilderAddBusy(true);
      setTmplItemsError(null);

      const res = await api<any>(
        "POST",
        `/api/material_templates/${encodeURIComponent(activeTemplateId)}/items`,
        { body: { material_library_ids: builderSelectedLibIds } }
      );

      if (!(res as any)?.ok)
        throw new Error((res as any)?.error || "Failed adding selected items");

      clearBuilderSelected();
      await loadTemplateItems(activeTemplateId);
    } catch (e: any) {
      setTmplItemsError(e?.message || "Failed adding items to template");
    } finally {
      setBuilderAddBusy(false);
    }
  }, [
    activeTemplateId,
    builderSelectedLibIds,
    clearBuilderSelected,
    loadTemplateItems,
    api,
    t,
  ]);

  const addAllCurrentFilterToTemplate = useCallback(async () => {
    if (!activeTemplateId) return window.alert(t("materials.selectTemplateBulk"));
    if (!libraryFiltered.length) return window.alert(t("materials.noLibraryItems"));

    const ids = libraryFiltered.map((m) => m.id).filter(Boolean);
    try {
      setBuilderAddBusy(true);
      setTmplItemsError(null);

      const res = await apiRequest<any>(
        "POST",
        `/api/material_templates/${encodeURIComponent(activeTemplateId)}/items`,
        { tenantId, body: { material_library_ids: ids } }
      );

      if (!(res as any)?.ok) throw new Error((res as any)?.error || "Failed adding items");

      clearBuilderSelected();
      await loadTemplateItems(activeTemplateId);
    } catch (e: any) {
      setTmplItemsError(e?.message || "Failed adding items to template");
    } finally {
      setBuilderAddBusy(false);
    }
  }, [activeTemplateId, libraryFiltered, clearBuilderSelected, loadTemplateItems, tenantId, t]);

  const removeFromTemplate = useCallback(
    async (materialLibraryId: string) => {
      if (!activeTemplateId) return;

      try {
        setTmplItemsBusy(true);
        setTmplItemsError(null);

        const res = await api<any>(
          "DELETE",
          `/api/material_templates/${encodeURIComponent(activeTemplateId)}/items/${encodeURIComponent(
            materialLibraryId
          )}`
        );

        if (!(res as any)?.ok)
          throw new Error((res as any)?.error || "Failed removing item");

        await loadTemplateItems(activeTemplateId);
      } catch (e: any) {
        setTmplItemsError(e?.message || "Failed removing item");
      } finally {
        setTmplItemsBusy(false);
      }
    },
    [activeTemplateId, loadTemplateItems, api]
  );

  // Delete a tenant-owned library item (hard delete per your current call)
  const deleteLibraryItem = useCallback(
    async (m: MaterialLibraryItem) => {
      if (!m?.id) return;
      const isGlobal = String(m.tenant_id || "") === "global";
      const isTenantOwned = String(m.tenant_id || "") === String(tenantId || "");

        // tenant can delete its own rows, and SYSTEM/NJDC can delete global rows
      const canDelete =
        (isTenantOwned && !isGlobal) || (isGlobal && canManageGlobalLibrary);

      if (!canDelete) {
       return window.alert(t("materials.globalOnlySystemNJDC"));
    }

      const ok = window.confirm(t("materials.confirmDeleteLibraryItem", { name: m.material_name }));

      if (!ok) return;

      try {
        setLibError(null);

        const res = await api<any>(
          "DELETE",
          `/api/material_library/${encodeURIComponent(m.id)}?hard=1`
        );

        if (!(res as any)?.ok) {
          throw new Error((res as any)?.error || "Failed to delete material");
        }

        // Refresh lists
        await loadLibrary();
        if (activeTemplateId) await loadTemplateItems(activeTemplateId);
      } catch (e: any) {
        setLibError(friendlyDeleteMessage(e, "Failed to delete material"));
      }
    },
        [tenantId, api, loadLibrary, activeTemplateId, loadTemplateItems, canManageGlobalLibrary, t]
  );

    // Library inline edit (modal)
  const [libEditId, setLibEditId] = useState<string | null>(null);
  const [libEditDraft, setLibEditDraft] = useState<{
    category: string;
    material_name: string;
    procurement_owner: ProcurementOwner;
    lead_time_days: string;
    link_url: string; // (display only for now; library doesn't store link_url unless you add it later)
  }>({
    category: "",
    material_name: "",
    procurement_owner: "contractor",
    lead_time_days: "",
    link_url: "",
  });

  const canEditLibraryRow = useCallback(
    (m: MaterialLibraryItem) => {
      const rowTenant = String(m?.tenant_id || "");
      if (rowTenant === String(tenantId || "")) return true;
      if (rowTenant === "global" && canManageGlobalLibrary) return true;
      return false;
    },
    [tenantId, canManageGlobalLibrary]
  );

  const beginEditLibraryRow = useCallback(
    (m: MaterialLibraryItem) => {
      setLibError(null);
      setLibEditId(m.id);
      setLibEditDraft({
        category: String(m.category || ""),
        material_name: String(m.material_name || ""),
        procurement_owner: (m.procurement_owner || "contractor") as ProcurementOwner,
        lead_time_days: m.lead_time_days == null ? "" : String(m.lead_time_days),
        link_url: String((m as any).link_url || ""),
      });
    },
    []
  );

  const cancelEditLibraryRow = useCallback(() => {
    setLibEditId(null);
    setLibEditDraft({
      category: "",
      material_name: "",
      procurement_owner: "contractor",
      lead_time_days: "",
      link_url: "",
    });
  }, []);

  const [libEditBusy, setLibEditBusy] = useState(false);

  const saveEditLibraryRow = useCallback(async () => {
    if (!libEditId) return;

    const name = moneylessTrim(libEditDraft.material_name);
    const cat = moneylessTrim(libEditDraft.category);
    if (!name) return window.alert(t("materials.materialRequired"));
    if (!cat) return window.alert(t("materials.categoryRequired"));

    const leadRaw = moneylessTrim(libEditDraft.lead_time_days);
    if (leadRaw && (Number.isNaN(Number(leadRaw)) || Number(leadRaw) < 0)) {
      return window.alert(t("materials.leadTimeInvalid"));
    }

    try {
      setLibEditBusy(true);
      setLibError(null);

      const body: any = {
        material_name: name,
        category: cat,
        procurement_owner: libEditDraft.procurement_owner,
        link_url: moneylessTrim(libEditDraft.link_url) || null,
      };
      if (libEditDraft.lead_time_days !== "") {
        body.lead_time_days = Number(leadRaw);
      } else {
        body.lead_time_days = null;
      }

      const res = await api<any>(
        "PATCH",
        `/api/material_library/${encodeURIComponent(libEditId)}`,
        { body }
      );

      if (!(res as any)?.ok) throw new Error((res as any)?.error || "Failed to update material");

      await loadLibrary();
      if (activeTemplateId) await loadTemplateItems(activeTemplateId);

      cancelEditLibraryRow();
    } catch (e: any) {
      setLibError(e?.message || "Failed to update material");
    } finally {
      setLibEditBusy(false);
    }
  }, [
    libEditId,
    libEditDraft,
    api,
    loadLibrary,
    activeTemplateId,
    loadTemplateItems,
    cancelEditLibraryRow,
    t,
  ]);


  // Add custom material (tenant library) + auto-add to template
  const [customName, setCustomName] = useState("");
  const [customCategory, setCustomCategory] = useState("");
  const [customOwner, setCustomOwner] = useState<ProcurementOwner>("contractor");
  const [customLeadDays, setCustomLeadDays] = useState("");
  const [customBusy, setCustomBusy] = useState(false);

  // Custom category UI: dropdown by default, with optional manual entry for new categories
  const [customCategoryMode, setCustomCategoryMode] = useState<"select" | "custom">(
    "select"
  );

  // If user picks a category filter, keep custom category aligned (and keep dropdown mode)
  useEffect(() => {
    if (builderCategory && !moneylessTrim(customCategory)) {
      setCustomCategory(builderCategory);
      setCustomCategoryMode("select");
    }
  }, [builderCategory, customCategory]);

  const addCustomToTemplate = useCallback(async () => {
    if (!activeTemplate) return window.alert(t("materials.selectTemplateBulk"));
    const name = moneylessTrim(customName);
    const cat = moneylessTrim(customCategory);
    if (!name) return window.alert(t("materials.materialRequired"));
    if (!cat) return window.alert(t("materials.categoryRequired"));

    const leadRaw = moneylessTrim(customLeadDays);
    const lead = leadRaw ? Number(leadRaw) : null;
    if (leadRaw && (Number.isNaN(lead) || (lead as number) < 0))
      return window.alert(t("materials.leadTimeInvalid"));

    try {
      setCustomBusy(true);
      setLibError(null);
      setTmplItemsError(null);

      // Create tenant library row (material_library requires template_code/name NOT NULL)
      const body: any = {
        tenant_id: tenantId,
        template_code: activeTemplate.template_code,
        template_name: activeTemplate.template_name,
        material_name: name,
        material_name_lc: lc(name),
        category: cat,
        procurement_owner: customOwner,
        lead_time_days: leadRaw ? lead : null,
        status: "active",
      };

      const resCreate = await apiRequest<any>("POST", `/api/material_library`, {
        tenantId,
        body,
      });
      if (!(resCreate as any)?.ok)
        throw new Error((resCreate as any)?.error || "Failed creating material");

      const newId = (resCreate as any)?.data?.id;
      if (!newId) throw new Error("Created material id not returned by API.");

      // Add to template membership
      const resAdd = await apiRequest<any>(
        "POST",
        `/api/material_templates/${encodeURIComponent(activeTemplate.id)}/items`,
        { tenantId, body: { material_library_ids: [newId] } }
      );
      if (!(resAdd as any)?.ok)
        throw new Error((resAdd as any)?.error || "Failed adding to template");

      // Refresh
      await loadLibrary();
      await loadTemplateItems(activeTemplate.id);

      // reset custom form
      setCustomName("");
      setCustomLeadDays("");
      // keep category as-is for fast entry
    } catch (e: any) {
      setTmplItemsError(e?.message || "Failed adding custom material");
    } finally {
      setCustomBusy(false);
    }
  }, [
    tenantId,
    activeTemplate,
    customName,
    customCategory,
    customOwner,
    customLeadDays,
    loadLibrary,
    loadTemplateItems,
    t,
  ]);

  /* ======================
     Project table: filters + selection + bulk
  ====================== */
  const [tableSearch, setTableSearch] = useState("");
  const [ownerFilter, setOwnerFilter] = useState<"" | ProcurementOwner>("");
  const [statusFilter, setStatusFilter] = useState<"" | MaterialStatus>("");

  const tableItems = useMemo(() => {
    const q = lc(tableSearch);
    let base = projectMaterials || [];

    if (ownerFilter) base = base.filter((m) => m.procurement_owner === ownerFilter);
    if (statusFilter) base = base.filter((m) => m.status === statusFilter);

    if (!q) return base;

    return base.filter((m) => {
      const text = `${m.category || ""} ${m.material_name || ""} ${m.procurement_owner || ""} ${m.status || ""} ${m.notes || ""} ${m.quantity ?? ""} ${m.link_url || ""} ${m.vendor || ""} ${m.order_ref || ""}`.toLowerCase();
      return text.includes(q);
    });
  }, [projectMaterials, tableSearch, ownerFilter, statusFilter]);

  const [selectedProjIds, setSelectedProjIds] = useState<string[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<MaterialStatus>("Not Ordered");

  useEffect(() => {
    setSelectedProjIds([]);
  }, [projectId]);

  const tableItemIds = useMemo(
    () => (tableItems || []).map((x) => x.id).filter(Boolean),
    [tableItems]
  );

  const allFilteredSelected = useMemo(() => {
    if (!tableItemIds.length) return false;
    return tableItemIds.every((id) => selectedProjIds.includes(id));
  }, [tableItemIds, selectedProjIds]);

  const someFilteredSelected = useMemo(() => {
    if (!tableItemIds.length) return false;
    return tableItemIds.some((id) => selectedProjIds.includes(id)) && !allFilteredSelected;
  }, [tableItemIds, selectedProjIds, allFilteredSelected]);

  const toggleProjSelected = useCallback((id: string) => {
    setSelectedProjIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const selectAllFiltered = useCallback(() => {
    setSelectedProjIds((prev) => {
      const set = new Set(prev);
      tableItemIds.forEach((id) => set.add(id));
      return Array.from(set.values());
    });
  }, [tableItemIds]);

  const clearSelectedProj = useCallback(() => setSelectedProjIds([]), []);

  const deleteSelectedProjectMaterials = useCallback(async () => {
    if (!projectId) return;
    if (!selectedProjIds.length) return window.alert(t("materials.selectAtLeastOneMat"));

    const ok = window.confirm(t("materials.confirmDeleteSelected", { count: selectedProjIds.length }));
    if (!ok) return;

    try {
      setBulkBusy(true);
      setProjError(null);

      for (const id of selectedProjIds) {
        const res = await apiRequest<any>(
          "DELETE",
          `/api/project_materials/${encodeURIComponent(id)}`,
          { tenantId }
        );
        if (!(res as any)?.ok)
          throw new Error((res as any)?.error || `Failed deleting material ${id}`);
      }

      setSelectedProjIds([]);
      await loadProjectMaterials(projectId);
    } catch (e: any) {
      setProjError(e?.message || "Failed to delete selected materials");
    } finally {
      setBulkBusy(false);
    }
  }, [tenantId, projectId, selectedProjIds, loadProjectMaterials, t]);

  const bulkUpdateStatusSelected = useCallback(async () => {
    if (!projectId) return;
    if (!selectedProjIds.length) return window.alert(t("materials.selectAtLeastOneMat"));

    try {
      setBulkBusy(true);
      setProjError(null);

      for (const id of selectedProjIds) {
        const res = await apiRequest<any>(
          "PATCH",
          `/api/project_materials/${encodeURIComponent(id)}`,
          { tenantId, body: { status: bulkStatus } }
        );
        if (!(res as any)?.ok)
          throw new Error((res as any)?.error || `Failed updating material ${id}`);
      }

      await loadProjectMaterials(projectId);
    } catch (e: any) {
      setProjError(e?.message || "Failed bulk status update");
    } finally {
      setBulkBusy(false);
    }
  }, [tenantId, projectId, selectedProjIds, bulkStatus, loadProjectMaterials, t]);

  /* ======================
     Project table: inline add + inline edit
  ====================== */
  const [rowEditId, setRowEditId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<EditDraft>(BLANK_DRAFT);

  const beginRowEdit = useCallback((m: ProjectMaterial) => {
    setRowEditId(m.id);
    setEditDraft({
      category: String(m.category || ""),
      material_name: String(m.material_name || ""),
      procurement_owner: (m.procurement_owner || "contractor") as ProcurementOwner,
      status: (m.status || "Not Ordered") as MaterialStatus,
      needed_by_date: String(m.needed_by_date || ""),
      actual_delivery_date: String(m.actual_delivery_date || ""),
      notes: String(m.notes || ""),
      quantity: m.quantity == null ? "" : String(m.quantity),
      link_url: String(m.link_url || ""),
    });
  }, []);

  const cancelRowEdit = useCallback(() => {
    setRowEditId(null);
    setEditDraft(BLANK_DRAFT);
  }, []);

  const [rowSaveBusy, setRowSaveBusy] = useState(false);

  const saveRowEdit = useCallback(async () => {
    if (!rowEditId) return;
    if (!projectId) return;

    const nameOk = moneylessTrim(editDraft.material_name).length > 0;
    if (!nameOk) return window.alert(t("materials.materialRequired"));
    const catOk = moneylessTrim(editDraft.category).length > 0;
    if (!catOk) return window.alert(t("materials.categoryRequired"));

    if (!isYmd(editDraft.needed_by_date))
      return window.alert(t("materials.needByInvalid"));
    if (!isYmd(editDraft.actual_delivery_date))
      return window.alert(t("materials.actualDateInvalid"));

    const qtyRaw = moneylessTrim(editDraft.quantity);
    if (qtyRaw && (Number.isNaN(Number(qtyRaw)) || Number(qtyRaw) < 0))
      return window.alert(t("materials.quantityInvalid"));

    try {
      setRowSaveBusy(true);
      setProjError(null);

      const body: any = {
        category: moneylessTrim(editDraft.category),
        material_name: moneylessTrim(editDraft.material_name),
        procurement_owner: editDraft.procurement_owner,
        status: editDraft.status,

        needed_by_date: moneylessTrim(editDraft.needed_by_date) || null,
        actual_delivery_date: moneylessTrim(editDraft.actual_delivery_date) || null,

        notes: moneylessTrim(editDraft.notes) || null,
        link_url: moneylessTrim(editDraft.link_url) || null,
        quantity: qtyRaw ? Number(qtyRaw) : null,
      };

      const res = await apiRequest<any>(
        "PATCH",
        `/api/project_materials/${encodeURIComponent(rowEditId)}`,
        { tenantId, body }
      );
      if (!(res as any)?.ok)
        throw new Error((res as any)?.error || "Failed to update material");

      cancelRowEdit();
      await loadProjectMaterials(projectId);
    } catch (e: any) {
      setProjError(e?.message || "Failed to save changes");
    } finally {
      setRowSaveBusy(false);
    }
  }, [tenantId, rowEditId, projectId, editDraft, cancelRowEdit, loadProjectMaterials, t]);

  // Inline add row at top
  const [showAddRow, setShowAddRow] = useState(false);
  const [addDraft, setAddDraft] = useState<EditDraft>(BLANK_DRAFT);

  useEffect(() => {
    // If project changes, hide add row
    setShowAddRow(false);
    setAddDraft(BLANK_DRAFT);
  }, [projectId]);

  const [addBusy, setAddBusy] = useState(false);

  const addNewProjectMaterial = useCallback(async () => {
    if (!projectId) return window.alert(t("materials.selectProjectFirst"));

    const nameOk = moneylessTrim(addDraft.material_name).length > 0;
    if (!nameOk) return window.alert(t("materials.materialRequired"));
    const catOk = moneylessTrim(addDraft.category).length > 0;
    if (!catOk) return window.alert(t("materials.categoryRequired"));

    if (!isYmd(addDraft.needed_by_date))
      return window.alert(t("materials.needByInvalid"));
    if (!isYmd(addDraft.actual_delivery_date))
      return window.alert(t("materials.actualDateInvalid"));

    const qtyRaw = moneylessTrim(addDraft.quantity);
    if (qtyRaw && (Number.isNaN(Number(qtyRaw)) || Number(qtyRaw) < 0))
      return window.alert(t("materials.quantityInvalid"));

    try {
      setAddBusy(true);
      setProjError(null);

      const body: any = {
        project_id: projectId,
        category: moneylessTrim(addDraft.category),
        material_name: moneylessTrim(addDraft.material_name),
        procurement_owner: addDraft.procurement_owner,
        status: addDraft.status,

        needed_by_date: moneylessTrim(addDraft.needed_by_date) || null,
        actual_delivery_date: moneylessTrim(addDraft.actual_delivery_date) || null,

        notes: moneylessTrim(addDraft.notes) || null,
        link_url: moneylessTrim(addDraft.link_url) || null,
        quantity: qtyRaw ? Number(qtyRaw) : null,
      };

      const res = await apiRequest<any>("POST", `/api/project_materials`, {
        tenantId,
        body,
      });
      if (!(res as any)?.ok)
        throw new Error((res as any)?.error || "Failed to add material");

      setShowAddRow(false);
      setAddDraft(BLANK_DRAFT);
      await loadProjectMaterials(projectId);
    } catch (e: any) {
      setProjError(e?.message || "Failed to add material");
    } finally {
      setAddBusy(false);
    }
  }, [tenantId, projectId, addDraft, loadProjectMaterials, t]);

  const deleteProjectMaterial = useCallback(
    async (id: string) => {
      const ok = window.confirm(t("materials.confirmDeleteMaterial"));
      if (!ok) return;

      try {
        setProjError(null);
        setProjBusy(true);

        const res = await apiRequest<any>(
          "DELETE",
          `/api/project_materials/${encodeURIComponent(id)}`,
          { tenantId }
        );
        if (!(res as any)?.ok)
          throw new Error((res as any)?.error || "Failed to delete project material");

        if (rowEditId === id) cancelRowEdit();
        await loadProjectMaterials(projectId);
      } catch (e: any) {
        setProjError(e?.message || "Failed to delete project material");
      } finally {
        setProjBusy(false);
      }
    },
    [tenantId, projectId, rowEditId, cancelRowEdit, loadProjectMaterials, t]
  );

  /* ======================
     Styles
     - Fix modal scroll: make modalCard flex-column + modalBody flex:1 + overflow:auto + min-height:0
     - Cleanup: removed duplicated/unused style blocks (duplicate .modalGrid, unused .tmplModalGrid, unused .span2)
  ====================== */
  const Styles = (
    <style>{`
      .matHeaderRow{
        display:flex;
        align-items:flex-start;
        justify-content:space-between;
        gap:12px;
        margin-bottom: 12px;
      }
      .matHeaderRight{
        display:flex;
        gap:10px;
        align-items:center;
        flex-wrap:wrap;
        justify-content:flex-end;
      }
      .muted{ color: var(--muted); font-weight: 800; font-size: 12px; }
      .field{ display:flex; flex-direction:column; gap:6px; }
      .label{ font-weight: 800; font-size: 13px; color: var(--text); }
      .reqStar{ color:#ff6b6b; margin-left:4px; }

      .input, .select, textarea{
        width:100%;
        border-radius:12px;
        border: 1px solid var(--border);
        background: rgba(255,255,255,0.92);
        padding: 10px 12px;
        color: var(--text);
        box-shadow: var(--shadow-sm);
        box-sizing: border-box;
      }
      textarea{ min-height: 70px; resize: vertical; }

      .btn{
        height: 40px;
        border-radius: 12px;
        border: 1px solid var(--border);
        padding: 0 14px;
        cursor:pointer;
        background: rgba(255,255,255,0.92);
        box-shadow: var(--shadow-sm);
        font-weight: 800;
        color: var(--text);
      }
      .btnPrimary{
        background: rgba(30, 64, 175, 0.10);
        border-color: rgba(30, 64, 175, 0.35);
      }
      .btnDanger{
        background: rgba(239, 68, 68, 0.10);
        border-color: rgba(239, 68, 68, 0.35);
      }
      .btn[disabled]{ cursor:not-allowed; opacity: 0.65; }

      .rowSplit{
        display:grid;
        grid-template-columns: 1fr 1fr;
        gap: 14px;
        align-items:start;
      }
      @media (max-width: 980px){
        .rowSplit{ grid-template-columns: 1fr; }
      }

      .pill{
        display:inline-flex;
        align-items:center;
        height: 22px;
        border-radius: 999px;
        padding: 0 10px;
        border: 1px solid var(--border);
        background: rgba(0,0,0,0.04);
        font-size: 11px;
        font-weight: 900;
        color: var(--text);
        white-space: nowrap;
      }
      .pillGood{ background: rgba(34,197,94,0.10); border-color: rgba(34,197,94,0.25); }
      .pillWarn{ background: rgba(245,158,11,0.12); border-color: rgba(245,158,11,0.25); }

      .errorText{
        margin-top: 10px;
        color:#b91c1c;
        font-weight: 900;
        white-space: pre-line;
      }

      .modalOverlay{
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,0.35);
        z-index: 50;

        display: flex;
        align-items: flex-start;
        justify-content: center;

        --topnavH: 72px;
        padding: calc(12px + var(--topnavH)) 12px 12px 12px;

        overflow: auto;
        -webkit-overflow-scrolling: touch;
      }

      .modalCard{
        width: min(1040px, 100%);
        border-radius: 16px;
        border: 1px solid var(--border);
        background: rgba(255,255,255,0.98);
        box-shadow: var(--shadow-sm);

        display: flex;
        flex-direction: column;
        padding: 14px;

        max-height: calc(100vh - (var(--topnavH) + 24px));
        overflow: hidden;           /* keep scroll inside modalBody */
        align-self: flex-start;
      }

      /* ✅ FIX: modal content now scrolls */
      .modalBody{
        flex: 1;
        min-height: 0;              /* critical for overflow to work inside flex column */
        overflow: auto;
        -webkit-overflow-scrolling: touch;
        margin-top: 12px;
        padding-right: 2px;         /* keeps scrollbar from clipping rounded edges */
      }

      .modalTitle{ font-weight: 900; font-size: 14px; margin: 0; color: var(--text); }
      .inlineRow{ display:flex; gap:10px; align-items:flex-end; flex-wrap:wrap; }
      .miniBtn{
        height: 34px;
        border-radius: 12px;
        border: 1px solid var(--border);
        padding: 0 10px;
        cursor:pointer;
        background: rgba(255,255,255,0.92);
        box-shadow: var(--shadow-sm);
        font-weight: 900;
        color: var(--text);
      }
      .panel{
        border: 1px solid var(--border);
        border-radius: 14px;
        background: rgba(255,255,255,0.86);
        box-shadow: var(--shadow-sm);
        padding: 10px;
      }
      .panelHeader{
        display:flex;
        align-items:center;
        justify-content:space-between;
        gap:10px;
        margin-bottom: 8px;
      }
      .panelHeader h4{ margin:0; font-size: 13px; font-weight: 1000; color: var(--text); }

      .tmplListItem{
        display:flex;
        align-items:center;
        justify-content:space-between;
        gap:10px;
        padding: 10px;
        border: 1px solid rgba(0,0,0,0.06);
        border-radius: 12px;
        background: rgba(0,0,0,0.01);
        cursor:pointer;
      }
      .tmplListItem.active{
        outline: 2px solid rgba(30,64,175,0.25);
        background: rgba(30,64,175,0.06);
      }
      .tmplMeta{ font-size: 12px; color: var(--muted); font-weight: 800; margin-top: 2px; }
      .badge{
        display:inline-flex;
        align-items:center;
        height: 22px;
        border-radius: 999px;
        padding: 0 10px;
        border: 1px solid var(--border);
        background: rgba(0,0,0,0.04);
        font-size: 11px;
        font-weight: 1000;
        white-space: nowrap;
      }

      .actionsInline{ display:flex; gap:8px; align-items:center; }
      .iconActionBtn{
        height: 30px;
        width: 30px;
        border-radius: 999px;
        display:grid;
        place-items:center;
        cursor:pointer;
        border: 1px solid var(--border);
        background: rgba(255,255,255,0.92);
        box-shadow: var(--shadow-sm);
      }
      .iconActionBtn.danger{
        background: rgba(239, 68, 68, 0.08);
        border-color: rgba(239, 68, 68, 0.25);
      }
      .iconGlyph{ font-size: 14px; line-height: 1; }

      .linkBtn{
        height: 30px;
        border-radius: 999px;
        padding: 0 10px;
        border: 1px solid var(--border);
        background: rgba(30, 64, 175, 0.08);
        cursor:pointer;
        font-weight: 900;
      }

      .tightInput{
        height: 34px;
        padding: 6px 10px;
        border-radius: 10px;
      }

      /* Modal grid (single source of truth — no duplicates) */
      .modalGrid{
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-areas:
          "newTemplate addCustom"
          "templateList buildTemplate"
          "addFromLibrary addFromLibrary";
        gap: 12px;
        align-items: start;
        width: 100%;
      }
      .modalGrid > *{ min-width: 0; }

      .areaNewTemplate{ grid-area: newTemplate; }
      .areaAddCustom{ grid-area: addCustom; }
      .areaTemplateList{ grid-area: templateList; }
      .areaBuildTemplate{ grid-area: buildTemplate; }
      .areaAddFromLibrary{ grid-area: addFromLibrary; }

      @media (max-width: 980px){
        .modalGrid{
          grid-template-columns: 1fr;
          grid-template-areas:
            "newTemplate"
            "addCustom"
            "templateList"
            "buildTemplate"
            "addFromLibrary";
        }
      }

      @media (max-width: 720px){
        .modalOverlay{ padding: calc(10px + var(--topnavH)) 10px 10px 10px; }
        .modalCard{ padding: 12px; border-radius: 14px; }
      }

      .tmplListScroll{
        display: grid;
        gap: 10px;
        max-height: 420px;
        overflow: auto;
        padding-right: 2px;
        width: 100%;
        min-width: 0;
        margin-top: 10px; /* ✅ adds spacing below search field */
      }

    /* Add-from-library table: stable widths + no horizontal scroll */
        .libTable{
        table-layout: fixed;
        width: 100%;
        }

        /* Make URL cell clamp regardless of inner wrapper */
        .libUrlCell{
        overflow: hidden;
        }
        .libUrlCell a{
        display: block;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        }

        /* Keep Actions tight and not stretched */
        .libActionsCell{
        white-space: nowrap;
        }


    `}</style>
  );

  /* ======================
     Render
  ====================== */
  return (
    <div className="container">
      {Styles}

      {/* Header */}
      <div className="matHeaderRow">
        <div>
          <h2 style={{ margin: 0 }}>{t("materials.title")}</h2>
          <div className="muted">{t("materials.subtitle")}</div>
        </div>

        <div className="matHeaderRight">
          <button
            className="btn"
            type="button"
            onClick={() => setShowTemplateBuilder(true)}
            disabled={tmplBusy || libBusy}
          >
            {t("materials.templatesLibraryBtn")}
          </button>
        </div>
      </div>

      {/* Card: Project + Apply template */}
      <Card
        title={t("materials.projectSetupTitle")}
        subtitle={t("materials.projectSetupSub")}
      >
        <div className="rowSplit">
          <div className="panel">
            <div className="field">
              <label className="label">
                {t("materials.projectLabel")} <span className="reqStar">*</span>
              </label>
              <select
                className="select"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
              >
                <option value="">{t("materials.selectProject")}</option>
                {(projects || []).map((p: any) => (
                  <option key={p.id} value={p.id}>
                    {projectDisplayLabelPretty(p as any, clients as any)}
                  </option>
                ))}
              </select>
              {projectId ? <div className="muted">{t("materials.selectedProject", { name: prettyProject })}</div> : null}
            </div>
          </div>

          <div className="panel">
            <div className="field">
              <label className="label">
                {t("materials.templateLabel")} <span className="reqStar">*</span>
              </label>
              <select
                className="select"
                value={activeTemplateId}
                onChange={(e) => setActiveTemplateId(e.target.value)}
                disabled={tmplBusy}
              >
                <option value="">{t("materials.selectTemplate")}</option>
                {(templates || []).map((tmpl) => (
                  <option key={tmpl.id} value={tmpl.id}>
                    {tmpl.template_name} ({tmpl.template_code}){" "}
                    {tmpl.tenant_id === "global" ? "— GLOBAL" : ""}
                  </option>
                ))}
              </select>
              {activeTemplate ? (
                <div className="muted">
                  Selected: <b>{activeTemplate.template_name}</b> •{" "}
                  {activeTemplate.template_code} •{" "}
                  {activeTemplate.tenant_id === "global"
                    ? "GLOBAL"
                    : String(activeTemplate.tenant_id || "").toUpperCase()}
                </div>
              ) : null}
            </div>

            <div className="inlineRow" style={{ justifyContent: "flex-end", marginTop: 10 }}>
              <button
                className="btn btnPrimary"
                type="button"
                onClick={applyTemplateToProjectV2}
                disabled={!projectId || !activeTemplateId || applyBusy}
                title={t("materials.addFromTemplateTip")}
              >
                {applyBusy ? t("materials.applying") : t("materials.applyTemplateBtn")}
              </button>
            </div>

            {tmplError ? <div className="errorText">{tmplError}</div> : null}
          </div>
        </div>

        {projError ? <div className="errorText">{projError}</div> : null}
      </Card>

      {/* Project Materials Table */}
      <div style={{ marginTop: 14 }}>
        <TableShell
          title={t("materials.projectMaterialsTitle")}
          subtitle={
            projectId
              ? t("materials.showingFor", { project: prettyProject })
              : t("materials.selectProjectToView")
          }
          right={
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <input
                className="input"
                style={{ height: 40, width: "min(360px, 72vw)" }}
                placeholder={t("materials.searchPlaceholder")}
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                disabled={!projectId}
              />

              <select
                className="select"
                style={{ height: 40, minWidth: 160 }}
                value={ownerFilter}
                onChange={(e) => setOwnerFilter((e.target.value as any) || "")}
                disabled={!projectId}
              >
                <option value="">{t("materials.allOwners")}</option>
                <option value="contractor">{t("materials.ownerContractor")}</option>
                <option value="client">{t("materials.ownerClient")}</option>
              </select>

              <select
                className="select"
                style={{ height: 40, minWidth: 160 }}
                value={statusFilter}
                onChange={(e) => setStatusFilter((e.target.value as any) || "")}
                disabled={!projectId}
              >
                <option value="">{t("materials.allStatus")}</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>

              <button
                className="btn"
                type="button"
                onClick={() => setShowAddRow((v) => !v)}
                disabled={!projectId || bulkBusy || projBusy || rowSaveBusy}
                title={t("materials.addOneOffTip")}
              >
                {showAddRow ? t("materials.hideAddRow") : t("materials.addMaterialBtn")}
              </button>

              {/* Bulk actions appear only when selection exists */}
              {selectedProjIds.length ? (
                <>
                  <span className="muted" style={{ fontWeight: 1000 }}>
                    {t("materials.selectedCount", { count: selectedProjIds.length })}
                  </span>

                  <select
                    className="select"
                    style={{ height: 40, minWidth: 160 }}
                    value={bulkStatus}
                    onChange={(e) => setBulkStatus(e.target.value as MaterialStatus)}
                    disabled={!projectId || bulkBusy}
                    title={t("materials.bulkStatusLabel")}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>

                  <button
                    className="btn btnPrimary"
                    type="button"
                    onClick={bulkUpdateStatusSelected}
                    disabled={!projectId || bulkBusy || !selectedProjIds.length}
                    title={t("materials.bulkStatusTip")}
                  >
                    {t("materials.applyStatusBtn")}
                  </button>

                  <button
                    className="btn btnDanger"
                    type="button"
                    onClick={deleteSelectedProjectMaterials}
                    disabled={!projectId || bulkBusy || !selectedProjIds.length}
                    title={t("materials.deleteSelectedBtn")}
                  >
                    {bulkBusy ? t("materials.loading") : t("materials.deleteSelectedBtn")}
                  </button>

                  <button
                    className="btn"
                    type="button"
                    onClick={clearSelectedProj}
                    disabled={!projectId || bulkBusy || !selectedProjIds.length}
                  >
                    {t("materials.clearSelectionBtn")}
                  </button>
                </>
              ) : null}
            </div>
          }
        >
          {/* (table section unchanged from your current file) */}
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 42 }}>
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someFilteredSelected;
                    }}
                    onChange={(e) => {
                      if (e.target.checked) selectAllFiltered();
                      else clearSelectedProj();
                    }}
                    disabled={!projectId || bulkBusy || !tableItems.length}
                    title={t("materials.selectAllFiltered")}
                  />
                </th>

                <th style={{ width: 150 }}>{t("materials.categoryCol")}</th>
                <th style={{ width: 260 }}>{t("materials.materialCol")}</th>
                <th style={{ width: 70 }}>{t("materials.qtyCol")}</th>
                <th style={{ width: 200 }}>{t("materials.urlCol")}</th>
                <th style={{ width: 120 }}>{t("materials.ownerCol")}</th>
                <th style={{ width: 140 }}>{t("materials.needByCol")}</th>
                <th style={{ width: 140 }}>{t("materials.actualCol")}</th>
                <th style={{ width: 140 }}>{t("materials.statusCol")}</th>
                <th>{t("materials.notesCol")}</th>
                <th style={{ width: 140 }} />
              </tr>
            </thead>
            <tbody>
              {!projectId ? (
                <tr>
                  <td colSpan={12} style={{ padding: 12 }}>
                    {t("materials.selectProjectFirst")}
                  </td>
                </tr>
              ) : projBusy ? (
                <tr>
                  <td colSpan={12} style={{ padding: 12 }}>
                    {t("materials.loading")}
                  </td>
                </tr>
              ) : (
                <>
                  {/* Inline Add Row */}
                  {showAddRow ? (
                    <>
                      <tr>
                        <td />
                        <td>
                          <input
                            className="input tightInput"
                            value={addDraft.category}
                            onChange={(e) =>
                              setAddDraft((d) => ({ ...d, category: e.target.value }))
                            }
                            placeholder={t("materials.categoryPlaceholder")}
                            disabled={addBusy}
                          />
                        </td>
                        <td>
                          <input
                            className="input tightInput"
                            value={addDraft.material_name}
                            onChange={(e) =>
                              setAddDraft((d) => ({
                                ...d,
                                material_name: e.target.value,
                              }))
                            }
                            placeholder={t("materials.materialPlaceholder")}
                            disabled={addBusy}
                          />
                        </td>
                        <td />
                        <td />
                        <td>
                          <select
                            className="select tightInput"
                            value={addDraft.procurement_owner}
                            onChange={(e) =>
                              setAddDraft((d) => ({
                                ...d,
                                procurement_owner: e.target.value as ProcurementOwner,
                              }))
                            }
                            disabled={addBusy}
                          >
                            <option value="contractor">{t("materials.ownerContractor")}</option>
                            <option value="client">{t("materials.ownerClient")}</option>
                          </select>
                        </td>

                        <td>
                          <input
                            className="input tightInput"
                            type="date"
                            value={addDraft.needed_by_date}
                            onChange={(e) =>
                              setAddDraft((d) => ({
                                ...d,
                                needed_by_date: e.target.value,
                              }))
                            }
                            disabled={addBusy}
                          />
                        </td>

                        <td />

                        <td>
                          <select
                            className="select tightInput"
                            value={addDraft.status}
                            onChange={(e) =>
                              setAddDraft((d) => ({
                                ...d,
                                status: e.target.value as MaterialStatus,
                              }))
                            }
                            disabled={addBusy}
                          >
                            {STATUS_OPTIONS.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td />

                        <td>
                          <div className="actionsInline">
                            <button
                              className="btn btnPrimary"
                              type="button"
                              onClick={addNewProjectMaterial}
                              disabled={addBusy}
                            >
                              {addBusy ? t("materials.adding") : t("materials.addBtn")}
                            </button>
                            <button
                              className="btn"
                              type="button"
                              onClick={() => {
                                setShowAddRow(false);
                                setAddDraft(BLANK_DRAFT);
                              }}
                              disabled={addBusy}
                            >
                              {t("materials.cancelBtn")}
                            </button>
                          </div>
                        </td>
                      </tr>

                      <tr>
                        <td />
                        <td colSpan={10}>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1.2fr 220px 220px 1.5fr",
                              gap: 10,
                            }}
                          >
                            <div>
                              <div className="muted">{t("materials.qtyLabel")}</div>
                              <input
                                className="input tightInput"
                                value={addDraft.quantity}
                                onChange={(e) =>
                                  setAddDraft((d) => ({
                                    ...d,
                                    quantity: e.target.value,
                                  }))
                                }
                                placeholder={t("materials.optionalPlaceholder")}
                                disabled={addBusy}
                              />
                            </div>

                            <div>
                              <div className="muted">{t("materials.linkLabel")}</div>
                              <input
                                className="input tightInput"
                                value={addDraft.link_url}
                                onChange={(e) =>
                                  setAddDraft((d) => ({
                                    ...d,
                                    link_url: e.target.value,
                                  }))
                                }
                                placeholder={t("materials.optionalPlaceholder")}
                                disabled={addBusy}
                              />
                            </div>

                            <div>
                              <div className="muted">{t("materials.actualLabel")}</div>
                              <input
                                className="input tightInput"
                                type="date"
                                value={addDraft.actual_delivery_date}
                                onChange={(e) =>
                                  setAddDraft((d) => ({
                                    ...d,
                                    actual_delivery_date: e.target.value,
                                  }))
                                }
                                disabled={addBusy}
                              />
                            </div>

                            <div style={{ gridColumn: "span 2" }}>
                              <div className="muted">{t("materials.notesLabel")}</div>
                              <input
                                className="input tightInput"
                                value={addDraft.notes}
                                onChange={(e) =>
                                  setAddDraft((d) => ({ ...d, notes: e.target.value }))
                                }
                                placeholder={t("materials.optionalPlaceholder")}
                                disabled={addBusy}
                              />
                            </div>
                          </div>
                        </td>
                        <td />
                      </tr>
                    </>
                  ) : null}

                  {/* Rows */}
                  {tableItems.length === 0 ? (
                    <tr>
                      <td colSpan={12} style={{ padding: 12 }}>
                        {t("materials.noMaterialsFound")}
                      </td>
                    </tr>
                  ) : (
                    tableItems.map((m) => {
                      const isEditing = rowEditId === m.id;

                      const statusPill =
                        m.status === "On Site" || m.status === "Installed"
                          ? "pill pillGood"
                          : m.status === "Ordered" || m.status === "In Transit"
                          ? "pill pillWarn"
                          : "pill";

                      return (
                        <React.Fragment key={m.id}>
                          <tr>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedProjIds.includes(m.id)}
                                onChange={() => toggleProjSelected(m.id)}
                                disabled={bulkBusy || rowSaveBusy || addBusy}
                                title={t("materials.selectBtn")}
                              />
                            </td>

                            <td>
                              {isEditing ? (
                                <input
                                  className="input tightInput"
                                  value={editDraft.category}
                                  onChange={(e) =>
                                    setEditDraft((d) => ({
                                      ...d,
                                      category: e.target.value,
                                    }))
                                  }
                                  disabled={rowSaveBusy}
                                />
                              ) : (
                                <CellClamp lines={1} title={m.category || ""}>
                                  {m.category || ""}
                                </CellClamp>
                              )}
                            </td>

                            <td>
                              {isEditing ? (
                                <input
                                  className="input tightInput"
                                  value={editDraft.material_name}
                                  onChange={(e) =>
                                    setEditDraft((d) => ({
                                      ...d,
                                      material_name: e.target.value,
                                    }))
                                  }
                                  disabled={rowSaveBusy}
                                />
                              ) : (
                                <CellClamp
                                  lines={1}
                                  title={`${m.material_name || ""}${
                                    isGlobalLibraryMaterial(m) ? " (G)" : ""
                                  }`}
                                >
                                  {m.material_name || ""}
                                  {isGlobalLibraryMaterial(m) ? (
                                    <span className="muted" style={{ marginLeft: 6 }}>
                                      (G)
                                    </span>
                                  ) : null}
                                </CellClamp>
                              )}
                            </td>

                            <td>{isEditing ? "" : m.quantity == null ? "" : String(m.quantity)}</td>

                            <td>
                              {isEditing ? (
                                ""
                              ) : normalizeUrl(m.link_url) ? (
                                <a
                                  href={normalizeUrl(m.link_url)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={m.link_url || ""}
                                  style={{ fontWeight: 900 }}
                                >
                                  <CellClamp lines={1} title={m.link_url || ""}>
                                    {m.link_url || ""}
                                  </CellClamp>
                                </a>
                              ) : (
                                ""
                              )}
                            </td>

                            <td>
                              {isEditing ? (
                                <select
                                  className="select tightInput"
                                  value={editDraft.procurement_owner}
                                  onChange={(e) =>
                                    setEditDraft((d) => ({
                                      ...d,
                                      procurement_owner: e.target.value as ProcurementOwner,
                                    }))
                                  }
                                  disabled={rowSaveBusy}
                                >
                                  <option value="contractor">{t("materials.ownerContractor")}</option>
                                  <option value="client">{t("materials.ownerClient")}</option>
                                </select>
                              ) : (m.procurement_owner || "") === "client" ? (
                                t("materials.ownerClient")
                              ) : (
                                t("materials.ownerContractor")
                              )}
                            </td>

                            <td>
                              {isEditing ? (
                                <input
                                  className="input tightInput"
                                  type="date"
                                  value={editDraft.needed_by_date}
                                  onChange={(e) =>
                                    setEditDraft((d) => ({
                                      ...d,
                                      needed_by_date: e.target.value,
                                    }))
                                  }
                                  disabled={rowSaveBusy}
                                />
                              ) : (
                                (m.needed_by_date as any) || ""
                              )}
                            </td>

                            <td>{isEditing ? "" : (m.actual_delivery_date as any) || ""}</td>

                            <td>
                              {isEditing ? (
                                <select
                                  className="select tightInput"
                                  value={editDraft.status}
                                  onChange={(e) =>
                                    setEditDraft((d) => ({
                                      ...d,
                                      status: e.target.value as MaterialStatus,
                                    }))
                                  }
                                  disabled={rowSaveBusy}
                                >
                                  {STATUS_OPTIONS.map((s) => (
                                    <option key={s} value={s}>
                                      {s}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <span className={statusPill}>
                                  {String(m.status || "").replace("_", " ")}
                                </span>
                              )}
                            </td>

                            <td>
                              {isEditing ? (
                                ""
                              ) : (
                                <CellClamp lines={2} title={m.notes || ""}>
                                  {m.notes || ""}
                                </CellClamp>
                              )}
                            </td>

                            <td>
                              <div className="actionsInline">
                                {isEditing ? (
                                  <>
                                    <button
                                      className="btn btnPrimary"
                                      type="button"
                                      onClick={saveRowEdit}
                                      disabled={rowSaveBusy}
                                    >
                                      {rowSaveBusy ? t("materials.saving") : t("materials.saveBtn")}
                                    </button>
                                    <button
                                      className="btn"
                                      type="button"
                                      onClick={cancelRowEdit}
                                      disabled={rowSaveBusy}
                                    >
                                      {t("materials.cancelBtn")}
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      className="iconActionBtn"
                                      type="button"
                                      title={t("materials.editBtn")}
                                      aria-label={t("materials.editBtn")}
                                      onClick={() => beginRowEdit(m)}
                                      disabled={bulkBusy || rowSaveBusy || addBusy}
                                    >
                                      <span className="iconGlyph">✏️</span>
                                    </button>
                                    <button
                                      className="iconActionBtn danger"
                                      type="button"
                                      title={t("materials.deleteBtn")}
                                      aria-label={t("materials.deleteBtn")}
                                      onClick={() => deleteProjectMaterial(m.id)}
                                      disabled={bulkBusy || rowSaveBusy || addBusy}
                                    >
                                      <span className="iconGlyph">🗑</span>
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>

                          {isEditing ? (
                            <tr>
                              <td />
                              <td colSpan={10}>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1.2fr 220px 220px 1.5fr",
                                    gap: 10,
                                  }}
                                >
                                  <div>
                                    <div className="muted">{t("materials.qtyLabel")}</div>
                                    <input
                                      className="input tightInput"
                                      value={editDraft.quantity}
                                      onChange={(e) =>
                                        setEditDraft((d) => ({
                                          ...d,
                                          quantity: e.target.value,
                                        }))
                                      }
                                      placeholder={t("materials.optionalPlaceholder")}
                                      disabled={rowSaveBusy}
                                    />
                                  </div>

                                  <div>
                                    <div className="muted">{t("materials.linkLabel")}</div>
                                    <input
                                      className="input tightInput"
                                      value={editDraft.link_url}
                                      onChange={(e) =>
                                        setEditDraft((d) => ({
                                          ...d,
                                          link_url: e.target.value,
                                        }))
                                      }
                                      placeholder={t("materials.optionalPlaceholder")}
                                      disabled={rowSaveBusy}
                                    />
                                  </div>

                                  <div>
                                    <div className="muted">{t("materials.actualLabel")}</div>
                                    <input
                                      className="input tightInput"
                                      type="date"
                                      value={editDraft.actual_delivery_date}
                                      onChange={(e) =>
                                        setEditDraft((d) => ({
                                          ...d,
                                          actual_delivery_date: e.target.value,
                                        }))
                                      }
                                      disabled={rowSaveBusy}
                                    />
                                  </div>

                                  <div style={{ gridColumn: "span 2" }}>
                                    <div className="muted">{t("materials.notesLabel")}</div>
                                    <input
                                      className="input tightInput"
                                      value={editDraft.notes}
                                      onChange={(e) =>
                                        setEditDraft((d) => ({ ...d, notes: e.target.value }))
                                      }
                                      placeholder={t("materials.optionalPlaceholder")}
                                      disabled={rowSaveBusy}
                                    />
                                  </div>
                                </div>
                              </td>
                              <td />
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })
                  )}
                </>
              )}
            </tbody>
          </table>
        </TableShell>
      </div>

      {/* =======================
          Templates & Library Modal (single)
      ======================= */}
      {showTemplateBuilder && (
        <div
          className="modalOverlay"
          role="dialog"
          aria-modal="true"
          aria-label={t("materials.templatesLibraryLabel")}
          onClick={() => {
            if (
              !tmplCreateBusy &&
              !tmplSaveBusy &&
              !builderAddBusy &&
              !customBusy &&
              !tmplItemsBusy
            ) {
              setShowTemplateBuilder(false);
            }
          }}
        >
          <div className="modalCard" onClick={(e) => e.stopPropagation()}>
            <div
              className="inlineRow"
              style={{ justifyContent: "space-between", alignItems: "center" }}
            >
              <div>
                <h3 className="modalTitle">{t("materials.templatesLibraryBtn")}</h3>
                <div className="muted" style={{ marginTop: 4 }}>
                  Build templates by selecting from the library (global + your tenant) and
                  adding your custom materials.
                </div>
              </div>
              <button
                type="button"
                className="miniBtn"
                onClick={() => {
                 cancelEditLibraryRow();
                 setShowTemplateBuilder(false);
                }}

                disabled={
                  tmplCreateBusy || tmplSaveBusy || builderAddBusy || customBusy || tmplItemsBusy
                }
              >
                {t("materials.closeBtn")}
              </button>
            </div>

            {/* ✅ scrollable area */}
            <div className="modalBody">
              <div className="modalGrid">
                {/* TOP LEFT: + New template */}
                <div className="panel areaNewTemplate">
                  <div className="panelHeader">
                    <h4 style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <button
                        type="button"
                        className="iconActionBtn"
                        aria-label={newTemplateOpen ? "Collapse" : "Expand"}
                        title={newTemplateOpen ? "Collapse" : "Expand"}
                        onClick={() => setNewTemplateOpen((v) => !v)}
                      >
                        <span className="iconGlyph">{newTemplateOpen ? "—" : "＋"}</span>
                      </button>
                      {t("materials.newTemplateTitle")}
                    </h4>
                    <span className="muted">{tmplCreateBusy ? t("materials.creatingTemplate") : ""}</span>
                  </div>

                {newTemplateOpen ? (
  
                  <div className="field">
                    <label className="label">
                      {t("materials.newTemplateTitle")} <span className="reqStar">*</span>
                    </label>
                    <input
                      className="input"
                      value={newTmplName}
                      onChange={(e) => setNewTmplName(e.target.value)}
                      placeholder={t("materials.templateNamePlaceholder")}
                      disabled={tmplCreateBusy}
                    />
                  </div>
                  ) : (
                    <div className="muted" style={{ marginTop: 6 }}>
                      Collapsed — expand to create a new template.
                    </div>
                  )}

                  {newTemplateOpen ? (
                    <div className="inlineRow" style={{ justifyContent: "flex-end", marginTop: 10 }}>
                        <button
                        className="btn btnPrimary"
                        type="button"
                        onClick={createTemplate}
                        disabled={tmplCreateBusy || !moneylessTrim(newTmplName)}
                        >
                        {tmplCreateBusy ? t("materials.creatingTemplate") : t("materials.createTemplateBtn")}
                        </button>
                    </div>
                  ) : null}

                  {tmplError ? <div className="errorText">{tmplError}</div> : null}
                </div>

                {/* TOP RIGHT: Add custom material (tenant) */}
                <div className="panel areaAddCustom">
                  <div className="panelHeader">
                    <h4 style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <button
                        type="button"
                        className="iconActionBtn"
                        aria-label={addCustomOpen ? "Collapse" : "Expand"}
                        title={addCustomOpen ? "Collapse" : "Expand"}
                        onClick={() => setAddCustomOpen((v) => !v)}
                      >
                        <span className="iconGlyph">{addCustomOpen ? "—" : "＋"}</span>
                      </button>
                      {t("materials.addCustomTitle")}
                    </h4>
                    <span className="muted">{customBusy ? t("materials.saving") : ""}</span>
                  </div>

                  {addCustomOpen ? (
                    !activeTemplate ? (
                        <div className="muted" style={{ marginTop: 8 }}>
                        {t("materials.selectTemplateFirst")}
                        </div>
                    ) : (
                    <>
                    <div className="rowSplit">
                        <div>
                        <div className="field">
                            <label className="label">
                            {t("materials.customNameLabel")} <span className="reqStar">*</span>
                            </label>
                            <input
                            className="input"
                            value={customName}
                            onChange={(e) => setCustomName(e.target.value)}
                            placeholder="e.g., Special vanity, Preferred tile…"
                            disabled={customBusy || !canEditTemplate(activeTemplate)}
                            />
                        </div>

                        <div className="field" style={{ marginTop: 10 }}>
                            <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 10,
                            }}
                            >
                            <label className="label">
                                {t("materials.customCategoryLabel")} <span className="reqStar">*</span>
                            </label>

                            <button
                                type="button"
                                className="iconActionBtn"
                                title={
                                customCategoryMode === "select"
                                    ? "Add a new category name"
                                    : "Use existing categories"
                                }
                                aria-label={t("materials.toggleCategoryMode")}
                                onClick={() =>
                                setCustomCategoryMode((m) => (m === "select" ? "custom" : "select"))
                                }
                                disabled={customBusy || !canEditTemplate(activeTemplate)}
                            >
                                <span className="iconGlyph">✏️</span>
                            </button>
                            </div>

                            {customCategoryMode === "select" ? (
                            <select
                                className="select"
                                value={customCategory}
                                onChange={(e) => {
                                const v = e.target.value;
                                if (v === "__new__") {
                                    setCustomCategory("");
                                    setCustomCategoryMode("custom");
                                } else {
                                    setCustomCategory(v);
                                }
                                }}
                                disabled={customBusy || !canEditTemplate(activeTemplate)}
                            >
                                <option value="">{t("materials.allCategories")}</option>
                                {(categories || []).map((c) => (
                                <option key={c} value={c}>
                                    {c}
                                </option>
                                ))}
                                <option value="__new__">+ Add new category…</option>
                            </select>
                            ) : (
                            <input
                                className="input"
                                value={customCategory}
                                onChange={(e) => setCustomCategory(e.target.value)}
                                placeholder={t("materials.newCategoryPlaceholder")}
                                disabled={customBusy || !canEditTemplate(activeTemplate)}
                            />
                            )}
                        </div>
                        </div>

                        <div>
                        <div className="field">
                            <label className="label">{t("materials.customOwnerLabel")}</label>
                            <select
                            className="select"
                            value={customOwner}
                            onChange={(e) => setCustomOwner(e.target.value as ProcurementOwner)}
                            disabled={customBusy || !canEditTemplate(activeTemplate)}
                            >
                            <option value="contractor">{t("materials.ownerContractor")}</option>
                            <option value="client">{t("materials.ownerClient")}</option>
                            </select>
                        </div>

                        <div className="field" style={{ marginTop: 10 }}>
                            <label className="label">{t("materials.customLeadDaysLabel")}</label>
                            <input
                            className="input"
                            value={customLeadDays}
                            onChange={(e) => setCustomLeadDays(e.target.value)}
                            placeholder={t("materials.optionalPlaceholder")}
                            disabled={customBusy || !canEditTemplate(activeTemplate)}
                            />
                        </div>
                        </div>
                    </div>

                    <div className="inlineRow" style={{ justifyContent: "flex-end", marginTop: 10 }}>
                        <button
                        className="btn btnPrimary"
                        type="button"
                        onClick={addCustomToTemplate}
                        disabled={
                            customBusy ||
                            !canEditTemplate(activeTemplate) ||
                            !moneylessTrim(customName) ||
                            !moneylessTrim(customCategory)
                        }
                        >
                        {customBusy ? t("materials.addingCustom") : t("materials.addCustomBtn")}
                        </button>
                    </div>

                    {!canEditTemplate(activeTemplate) ? (
                        <div className="muted" style={{ marginTop: 8 }}>
                        You can’t modify this template (global templates editable only in SYSTEM).
                        </div>
                    ) : null}
                    </>
                   )
                  ) : (
                    <div className="muted" style={{ marginTop: 6 }}>
                        Collapsed — expand to add a custom material.
                    </div>
                  )}
                </div>

                {/* MIDDLE LEFT: Template list */}
                <div className="panel areaTemplateList">
                  <div className="panelHeader">
                    <h4>{t("materials.templateListTitle")}</h4>
                    <span className="muted">
                      {tmplBusy ? t("materials.loading") : `${(templates || []).length} total`}
                    </span>
                  </div>

                  <div className="field">
                    <label className="label">{t("materials.searchTemplates")}</label>
                    <input
                      className="input"
                      value={tmplSearch}
                      onChange={(e) => setTmplSearch(e.target.value)}
                      placeholder={t("materials.searchTemplates")}
                      disabled={tmplBusy || tmplCreateBusy || tmplSaveBusy}
                    />
                  </div>

                  <div className="tmplListScroll">
                    {templatesFiltered.length === 0 ? (
                      <div className="muted">{t("materials.noTemplates")}</div>
                    ) : (
                      templatesFiltered.map((tmpl) => {
                        const isActive = tmpl.id === activeTemplateId;
                        const badge =
                          tmpl.tenant_id === "global"
                            ? "GLOBAL"
                            : String(tmpl.tenant_id || "").toUpperCase();
                        const editable = canEditTemplate(tmpl);

                        return (
                          <div
                            key={tmpl.id}
                            className={`tmplListItem ${isActive ? "active" : ""}`}
                            onClick={() => setActiveTemplateId(tmpl.id)}
                            role="button"
                            aria-label={`Select template ${tmpl.template_name}`}
                          >
                            <div style={{ minWidth: 0 }}>
                              <div
                                style={{
                                  fontWeight: 1000,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {tmpl.template_name} <span className="muted">({tmpl.template_code})</span>
                              </div>
                              <div className="tmplMeta">
                                <span className="badge">{badge}</span>
                              </div>
                            </div>

                            <div className="actionsInline">
                              <button
                                className="iconActionBtn"
                                type="button"
                                title={editable ? "Rename template" : "No permission"}
                                aria-label={t("materials.renameBtn")}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!editable) return;
                                  beginEditTemplate(tmpl);
                                }}
                                disabled={!editable || tmplSaveBusy}
                              >
                                <span className="iconGlyph">✏️</span>
                              </button>

                              <button
                                className="iconActionBtn danger"
                                type="button"
                                title={editable ? "Delete template" : "No permission"}
                                aria-label={t("materials.deleteBtn")}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!editable) return;
                                  deleteTemplate(tmpl);
                                }}
                                disabled={!editable || tmplSaveBusy}
                              >
                                <span className="iconGlyph">🗑</span>
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {tmplError ? <div className="errorText">{tmplError}</div> : null}
                </div>

                {/* MIDDLE RIGHT: Build template */}
                <div className="panel areaBuildTemplate">
                  <div className="panelHeader">
                    <h4>{t("materials.buildTemplateTitle")}</h4>
                    <span className="muted">
                      {activeTemplate
                        ? `${activeTemplate.template_name} (${activeTemplate.template_code})`
                        : t("materials.selectTemplateFirst")}
                    </span>
                  </div>

                  {!activeTemplate ? (
                    <div className="muted" style={{ marginTop: 8 }}>
                      Select a template on the left to add library items and custom materials.
                    </div>
                  ) : (
                    <>
                      {/* Rename editor inline */}
                      {editTmplId && editTmplId === activeTemplate.id ? (
                        <div className="panel" style={{ marginBottom: 12 }}>
                          <div className="panelHeader">
                            <h4>{t("materials.editLibraryTitle")}</h4>
                            <span className="muted">{tmplSaveBusy ? t("materials.saving") : ""}</span>
                          </div>

                          <div className="rowSplit">
                            <div className="field">
                              <label className="label">
                                {t("materials.newTemplateTitle")} <span className="reqStar">*</span>
                              </label>
                              <input
                                className="input"
                                value={editTmplName}
                                onChange={(e) => setEditTmplName(e.target.value)}
                                disabled={tmplSaveBusy}
                              />
                            </div>

                            <div className="field">
                              <label className="label">
                                {t("materials.templateCodeLabel")} <span className="reqStar">*</span>
                              </label>
                              <input
                                className="input"
                                value={editTmplCode}
                                onChange={(e) => setEditTmplCode(e.target.value)}
                                disabled={tmplSaveBusy}
                              />
                            </div>
                          </div>

                          <div className="inlineRow" style={{ justifyContent: "flex-end", marginTop: 10 }}>
                            <button className="btn" type="button" onClick={cancelEditTemplate} disabled={tmplSaveBusy}>
                              {t("materials.cancelBtn")}
                            </button>
                            <button className="btn btnPrimary" type="button" onClick={saveTemplateEdit} disabled={tmplSaveBusy}>
                              {t("materials.saveBtn")}
                            </button>
                          </div>
                        </div>
                      ) : null}

                      {/* Template contents */}
                      <div className="panel" style={{ marginTop: 12 }}>
                        <div className="panelHeader">
                          <h4>{t("materials.buildTemplateTitle")}</h4>
                          <span className="muted">
                            {tmplItemsBusy ? t("materials.loading") : t("materials.templateItemCount", { count: (templateItems || []).length })}
                          </span>
                        </div>

                        <div
                          style={{
                            maxHeight: 240,
                            overflow: "auto",
                            border: "1px solid rgba(0,0,0,0.06)",
                            borderRadius: 12,
                          }}
                        >
                          <table className="table" style={{ margin: 0 }}>
                            <thead>
                              <tr>
                                <th style={{ width: 160 }}>{t("materials.libColCategory")}</th>
                                <th>{t("materials.libColMaterial")}</th>
                                <th style={{ width: 120 }}>{t("materials.libColOwner")}</th>
                                <th style={{ width: 56 }} />
                              </tr>
                            </thead>
                            <tbody>
                              {tmplItemsBusy ? (
                                <tr>
                                  <td colSpan={4} style={{ padding: 12 }}>
                                    Loading…
                                  </td>
                                </tr>
                              ) : templateItems.length === 0 ? (
                                <tr>
                                  <td colSpan={4} style={{ padding: 12 }}>
                                    No items yet. Add from library or add custom above.
                                  </td>
                                </tr>
                              ) : (
                                templateItems.map((m) => (
                                  <tr key={m.id}>
                                    <td>
                                      <CellClamp lines={1} title={m.category || ""}>
                                        {m.category || ""}
                                      </CellClamp>
                                    </td>
                                    <td>
                                      <CellClamp lines={1} title={m.material_name || ""}>
                                        {m.material_name || ""}
                                      </CellClamp>
                                      <div className="muted">
                                        {m.tenant_id === "global"
                                          ? "GLOBAL"
                                          : String(m.tenant_id || "").toUpperCase()}
                                      </div>
                                    </td>
                                    <td>{m.procurement_owner === "client" ? t("materials.ownerClient") : t("materials.ownerContractor")}</td>
                                    <td>
                                      <button
                                        className="iconActionBtn danger"
                                        type="button"
                                        title={canEditTemplate(activeTemplate) ? t("materials.removeFromTemplate") : t("materials.selectTemplateFirst")}
                                        aria-label={t("materials.removeBtn")}
                                        onClick={() => removeFromTemplate(m.id)}
                                        disabled={!canEditTemplate(activeTemplate) || tmplItemsBusy}
                                      >
                                        <span className="iconGlyph">🗑</span>
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>

                        {tmplItemsError ? <div className="errorText">{tmplItemsError}</div> : null}
                      </div>
                    </>
                  )}
                </div>

                {/* BOTTOM: Add from library */}
                <div className="panel areaAddFromLibrary">
                  <div className="panelHeader">
                    <h4>{t("materials.addFromLibraryTitle")}</h4>
                    <span className="muted">
                      {builderAddBusy ? t("materials.loading") : `${libraryFiltered.length} shown`}
                    </span>
                  </div>

                  {!activeTemplate ? (
                    <div className="muted" style={{ marginTop: 8 }}>
                      {t("materials.selectTemplateFirst")}
                    </div>
                   ) : (
                    <>
                    <div
                      className="inlineRow"
                        style={{
                            alignItems: "flex-end",
                            flexWrap: "nowrap",
                            gap: 10,
                        }}
                        >
                        <div className="field" style={{ minWidth: 170, flex: "0 0 170px" }}>
                            <label className="label">{t("materials.categoryFilter")}</label>
                            <select
                            className="select"
                            value={builderCategory}
                            onChange={(e) => {
                                setBuilderCategory(e.target.value);
                                setBuilderSelectedLibIds([]);
                            }}
                            disabled={libBusy || builderAddBusy}
                            >
                            <option value="">{t("materials.allCategories")}</option>
                            {categories.map((c) => (
                                <option key={c} value={c}>
                                {c}
                                </option>
                            ))}
                            </select>
                        </div>

                        <div className="field" style={{ minWidth: 220, flex: "1 1 260px" }}>
                            <label className="label">{t("materials.searchLibrary")}</label>
                            <input
                            className="input"
                            value={builderSearch}
                            onChange={(e) => {
                                setBuilderSearch(e.target.value);
                                setBuilderSelectedLibIds([]);
                            }}
                            placeholder={t("materials.searchLibrary")}
                            disabled={libBusy || builderAddBusy}
                            />
                        </div>

                        <div
                            style={{
                            display: "flex",
                            gap: 10,
                            alignItems: "center",
                            justifyContent: "flex-end",
                            flex: "0 0 auto",
                            paddingBottom: 2,
                            flexWrap: "nowrap",
                            whiteSpace: "nowrap",
                            }}
                        >
                            <button
                            className="btn"
                            type="button"
                            onClick={selectAllBuilderFiltered}
                            disabled={libBusy || builderAddBusy || !libraryFiltered.length}
                            >
                            {t("materials.selectAllFiltered")}
                            </button>

                            <button
                            className="btn"
                            type="button"
                            onClick={clearBuilderSelected}
                            disabled={builderAddBusy || !builderSelectedLibIds.length}
                            >
                            {t("materials.clearSelectionBtn")}
                            </button>

                            <button
                            className="btn"
                            type="button"
                            onClick={addAllCurrentFilterToTemplate}
                            disabled={libBusy || builderAddBusy || !libraryFiltered.length}
                            title={t("materials.addAllFilteredBtn")}
                            >
                            {t("materials.addAllFilteredBtn")}
                            </button>

                            <button
                            className="btn btnPrimary"
                            type="button"
                            onClick={addSelectedToTemplate}
                            disabled={libBusy || builderAddBusy || !builderSelectedLibIds.length}
                            >
                            {t("materials.addToTemplateBtn")} ({builderSelectedLibIds.length})
                            </button>
                        </div>
                   </div>


                      {libEditId ? (
                        <div className="panel" style={{ marginTop: 10 }}>
                            <div className="panelHeader">
                            <h4>{t("materials.editLibraryTitle")}</h4>
                            <span className="muted">{libEditBusy ? t("materials.saving") : ""}</span>
                            </div>

                            <div className="rowSplit">
                            <div className="field">
                                <label className="label">
                                {t("materials.customNameLabel")} <span className="reqStar">*</span>
                                </label>
                                <input
                                className="input"
                                value={libEditDraft.material_name}
                                onChange={(e) =>
                                    setLibEditDraft((d) => ({ ...d, material_name: e.target.value }))
                                }
                                disabled={libEditBusy}
                                />
                            </div>

                            <div className="field">
                                <label className="label">
                                {t("materials.customCategoryLabel")} <span className="reqStar">*</span>
                                </label>
                                <input
                                className="input"
                                value={libEditDraft.category}
                                onChange={(e) => setLibEditDraft((d) => ({ ...d, category: e.target.value }))}
                                disabled={libEditBusy}
                                />
                            </div>
                            </div>

                            <div className="rowSplit" style={{ marginTop: 10 }}>
                            <div className="field">
                                <label className="label">{t("materials.libColOwner")}</label>
                                <select
                                className="select"
                                value={libEditDraft.procurement_owner}
                                onChange={(e) =>
                                    setLibEditDraft((d) => ({
                                    ...d,
                                    procurement_owner: e.target.value as ProcurementOwner,
                                    }))
                                }
                                disabled={libEditBusy}
                                >
                                <option value="contractor">{t("materials.ownerContractor")}</option>
                                <option value="client">{t("materials.ownerClient")}</option>
                                </select>
                            </div>

                            <div className="field">
                                <label className="label">{t("materials.customLeadDaysLabel")}</label>
                                <input
                                className="input"
                                value={libEditDraft.lead_time_days}
                                onChange={(e) =>
                                    setLibEditDraft((d) => ({ ...d, lead_time_days: e.target.value }))
                                }
                                disabled={libEditBusy}
                                placeholder={t("materials.optionalPlaceholder")}
                                />
                            </div>
                            </div>
                            <div className="field" style={{ marginTop: 10 }}>
                                <label className="label">{t("materials.urlCol")}</label>
                                <input
                                    className="input"
                                    value={libEditDraft.link_url}
                                    onChange={(e) => setLibEditDraft((d) => ({ ...d, link_url: e.target.value }))}
                                    placeholder="https://… (optional)"
                                    disabled={libEditBusy}
                                />
                            </div>
                            <div className="inlineRow" style={{ justifyContent: "flex-end", marginTop: 10 }}>
                            <button className="btn" type="button" onClick={cancelEditLibraryRow} disabled={libEditBusy}>
                                {t("materials.cancelBtn")}
                            </button>
                            <button className="btn btnPrimary" type="button" onClick={saveEditLibraryRow} disabled={libEditBusy}>
                                {t("materials.saveBtn")}
                            </button>
                            </div>
                        </div>
                      ) : null}

                      <div
                        style={{
                          marginTop: 10,
                          maxHeight: 260,
                          overflow: "auto",
                          border: "1px solid rgba(0,0,0,0.06)",
                          borderRadius: 12,
                        }}
                      >
                        <table className="table" style={{ margin: 0, width: "100%", tableLayout: "fixed" }}>
                          <thead>
                            <tr>
                                {/* ✅ select column (checkboxes) */}
                                <th style={{ width: 23 }} />

                                <th style={{ width: 150 }}>{t("materials.libColCategory")}</th>

                                {/* ✅ Material should take remaining space */}
                                <th>{t("materials.libColMaterial")}</th>

                                <th style={{ width: 75 }}>{t("materials.libColOwner")}</th>
                                <th style={{ width: 25 }}>{t("materials.inTemplate")}</th>

                                {/* ✅ URL narrower so Actions stays visible */}
                                <th style={{ width: 220 }}>{t("materials.libColUrl")}</th>

                                <th style={{ width: 75 }}>{t("materials.libColActions")}</th>
                            </tr>
                          </thead>

                          <tbody>
                            {libBusy ? (
                              <tr>
                                <td colSpan={7} style={{ padding: 12 }}>

                                  Loading library…
                                </td>
                              </tr>
                            ) : libraryFiltered.length === 0 ? (
                              <tr>
                                <td colSpan={7} style={{ padding: 12 }}>
                                  No library materials match.
                                </td>
                              </tr>
                            ) : (
                              libraryFiltered.map((m) => {
                                const inTemplate = templateItemIdSet.has(m.id);
                                const checked = builderSelectedLibIds.includes(m.id);
                                return (
                                  <tr key={m.id}>
                                    <td>
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleBuilderPick(m.id)}
                                        disabled={builderAddBusy}
                                        title={t("materials.selectBtn")}
                                      />
                                    </td>
                                    <td>
                                      <CellClamp lines={1} title={m.category || ""}>
                                        {m.category || ""}
                                      </CellClamp>
                                    </td>
                                    <td>
                                        <CellClamp
                                            lines={1}
                                            title={`${m.material_name || ""}${m.tenant_id === "global" ? " (G)" : ""}`}
                                        >
                                            {m.material_name || ""}
                                            {m.tenant_id === "global" ? (
                                            <span className="muted" style={{ marginLeft: 6 }}>(G)</span>
                                            ) : null}
                                        </CellClamp>
                                    </td>

                                    <td>{m.procurement_owner === "client" ? t("materials.ownerClient") : t("materials.ownerContractor")}</td>
                                    <td>{inTemplate ? "✅" : ""}</td>

                                    <td className="libUrlCell">
                                        {normalizeUrl((m as any).link_url) ? (
                                            <a
                                            href={normalizeUrl((m as any).link_url)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title={(m as any).link_url || ""}
                                            style={{ fontWeight: 900 }}
                                            >
                                            <CellClamp lines={1} title={(m as any).link_url || ""}>
                                                {(m as any).link_url || ""}
                                            </CellClamp>
                                            </a>
                                        ) : (
                                            ""
                                        )}
                                    </td>


                                    
                                    <td className="libActionsCell">
                                        <div className="actionsInline">
                                            {canEditLibraryRow(m) ? (
                                            <button
                                                className="iconActionBtn"
                                                type="button"
                                                title={t("materials.editBtn")}
                                                aria-label={t("materials.editBtn")}
                                                onClick={() => beginEditLibraryRow(m)}
                                                disabled={builderAddBusy || libBusy || libEditBusy}
                                            >
                                                <span className="iconGlyph">✏️</span>
                                            </button>
                                            ) : null}

                                            {((m.tenant_id === tenantId) || (m.tenant_id === "global" && canManageGlobalLibrary)) ? (
                                            <button
                                              className="iconActionBtn danger"
                                              type="button"
                                              title={t("materials.deleteBtn")}
                                              aria-label={t("materials.deleteBtn")}
                                              onClick={() => deleteLibraryItem(m)}
                                              disabled={builderAddBusy || libBusy || libEditBusy}
                                            >
                                              <span className="iconGlyph">🗑</span>
                                            </button>

                                            ) : null}
                                        </div>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>

                      {libError ? <div className="errorText">{libError}</div> : null}
                    </>
                  )}
                </div>
              </div>

              {tmplError ? <div className="errorText">{tmplError}</div> : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
